import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import { useSelector } from 'react-redux'; // Import hook
import { useSearchParams } from 'react-router-dom';
import EmojiPicker from 'emoji-picker-react';
import SearchBar from '../../components/Common/SearchBar/SearchBar'; // Added SearchBar import
import './ChatSystem.css';

// FontAwesome Icons
import {
  faPlus,
  faEllipsisV,
  faSearch,
  faInfoCircle,
  faPhone,
  faVideo,
  faImage,
  faSmile,
  faPaperPlane,
  faPlane,
  faPen,
  faTrash,
  faTimes,
  faCheck,
  faCheckDouble,
  faPalette,
  faPaperclip,
  faChevronDown,
  faPlay,
  faPause,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { getApiOrigin, resolveChatAttachmentUrl } from '../../utils/assetUrls';
import { canRoleSendChatMessages, canSendMessageToRole, getChatRoleDisplay, getChatRoleBadgeClass, buildChatRecipientRoleTabs } from '../../utils/chatPermissions';
import { ROLES } from '../../utils/roleUtils';
import practiceService from '../../services/practiceService';
import patientService from '../../services/patientService';
import { checkBPValues, checkBGValue, checkWeightValue, getVitalStatusColor } from '../../utils/measurementUtils';
import {
  getBloodPressureTargetForMeasurement,
  getBloodGlucoseTargetForMeasurement,
  resolveEffectiveScheduleTargets,
  hasAnyScheduleTargets,
} from '../../utils/scheduleTargetUtils';
import chatService, {
  dispatchChatUnreadRefresh,
  CHAT_MESSAGE_EVENT,
} from '../../services/chatService';
import {
  isWithinChatMessageActionWindow,
  isMessageEdited,
  chatActionWindowExpiredMessage,
} from '../../utils/chatMessageRules';
import AddFollowUpModal from '../../components/AddFollowUpModal/AddFollowUpModal';
import ConfirmDeleteModal from '../../components/Common/ConfirmDeleteModal/ConfirmDeleteModal';
import { toast } from 'react-toastify';
import { matchesWordStart, matchesWordStartAny } from '../../utils/searchMatch';

const SOCKET_URL =
  typeof window !== 'undefined' ? getApiOrigin() : 'http://localhost:3000';

const CHAT_SEND_BLOCKED_MESSAGE =
  'You are not allowed to send a message to this user.';

const formatChatSessionTimer = (totalSeconds) => {
  const mins = Math.floor(Math.max(0, totalSeconds) / 60);
  const secs = Math.max(0, totalSeconds) % 60;
  return `${mins.toString().padStart(2, '0')} : ${secs.toString().padStart(2, '0')}`;
};

const sameUserId = (a, b) => {
  if (a == null || b == null) return false;
  return Number(a) === Number(b);
};

const messageBelongsToChat = (msg, chat) => {
  if (!msg || !chat) return false;
  const otherId = chat.other_user_id;
  return sameUserId(msg.from_user_id, otherId) || sameUserId(msg.to_user_id, otherId);
};

const mergeChatMessage = (prev, msg) => {
  if (!msg) return prev;
  if (msg.id != null && prev.some((m) => Number(m.id) === Number(msg.id))) {
    return prev.map((m) =>
      Number(m.id) === Number(msg.id) ? { ...m, ...msg, _pending: false } : m
    );
  }
  const cleaned = prev.filter((m) => {
    if (!m._pending) return true;
    if (!sameUserId(m.from_user_id, msg.from_user_id)) return true;
    if (!sameUserId(m.to_user_id, msg.to_user_id)) return true;
    const pendingFile = Boolean(m.file_path || m._pendingFile);
    const incomingFile = Boolean(msg.file_path);
    if (pendingFile && incomingFile) return false;
    if (String(m.message || '') !== String(msg.message || '')) return true;
    return false;
  });
  return [...cleaned, msg];
};

const formatChatFileSize = (bytes) => {
  const size = Number(bytes) || 0;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const CHAT_DEFAULT_TARGETS = {
  bloodPressureTargetRange: [{
    systolicMin: 100,
    systolicMax: 140,
    diastolicMin: 60,
    diastolicMax: 90,
    pulseMin: 60,
    pulseMax: 100,
  }],
  bloodGlucoseTargetRange: [{
    minimum: 60,
    maximum: 110,
  }],
  weightTargetRange: {
    weightMin: 66,
    weightMax: 220,
  },
};

const snapshotAlertClass = (status) => {
  if (status === 'high') return 'alert-high';
  if (status === 'low') return 'alert-low';
  if (status === 'normal') return 'alert-normal';
  return '';
};

const snapshotReadingStyle = (status) => {
  if (!status) return undefined;
  return { color: getVitalStatusColor(status) };
};

const roundSnapshotReading = (value) => {
  const n = Number(value);
  return value != null && value !== '' && !Number.isNaN(n) ? Math.round(n) : value;
};

const getSnapshotVitalStatuses = (vitals, scheduleTargets) => {
  const targets = scheduleTargets || CHAT_DEFAULT_TARGETS;
  const bpData = vitals?.bp
    ? {
        systolic_pressure: vitals.bp.systolic,
        diastolic_pressure: vitals.bp.diastolic,
        measure_date_time: vitals.bp.measured_at,
      }
    : null;
  const bgData = vitals?.glucose
    ? {
        blood_glucose_value_1: vitals.glucose.value,
        measure_date_time: vitals.glucose.measured_at,
      }
    : null;
  const weightData = vitals?.weight
    ? { weight: vitals.weight.value }
    : null;

  const bpTarget =
    getBloodPressureTargetForMeasurement(bpData || {}, targets) ||
    CHAT_DEFAULT_TARGETS.bloodPressureTargetRange[0];
  const bgTarget =
    getBloodGlucoseTargetForMeasurement(bgData || {}, targets) ||
    CHAT_DEFAULT_TARGETS.bloodGlucoseTargetRange[0];
  const weightTarget =
    (targets.weightTargetRange && Object.keys(targets.weightTargetRange).length > 0
      ? targets.weightTargetRange
      : null) || CHAT_DEFAULT_TARGETS.weightTargetRange;

  return {
    bp: bpData ? checkBPValues(bpData, bpTarget) : null,
    glucose: bgData ? checkBGValue(bgData, bgTarget) : null,
    weight: weightData ? checkWeightValue(Number(weightData.weight), weightTarget) : null,
  };
};

const ChatRoleBadge = ({ roleId, roleName, className = '' }) => {
  const label = getChatRoleDisplay(roleId, roleName);
  const badgeClass = getChatRoleBadgeClass(roleId);
  return (
    <span className={`chat-role-badge ${badgeClass} ${className}`.trim()}>{label}</span>
  );
};

function parsePracticeIds(value) {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map(Number).filter((id) => Number.isFinite(id) && id > 0);
  }
  return String(value)
    .split(',')
    .map((part) => Number(String(part).trim()))
    .filter((id) => Number.isFinite(id) && id > 0);
}

function parsePracticeNames(value) {
  if (!value) return [];
  return String(value)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

function formatPracticeSummary(practiceName, { full = false } = {}) {
  const names = parsePracticeNames(practiceName);
  if (names.length === 0) return null;
  if (full || names.length === 1) return names.join(', ');
  return `${names.length} practices`;
}

const ChatSystem = () => {
  // Redux State
  const { user: reduxUser } = useSelector((state) => state.auth);

  // Local State
  const [currentUser, setCurrentUser] = useState(null);
  const canSendMessages = canRoleSendChatMessages(
    currentUser?.role_id ?? reduxUser?.role_id
  );
  const senderRoleId = currentUser?.role_id ?? reduxUser?.role_id;
  const [conversations, setConversations] = useState([]);
  const [currentChat, setCurrentChat] = useState(null);
  const [deleteModalState, setDeleteModalState] = useState({
    show: false,
    title: 'Confirm Delete',
    message: '',
    warning: 'This action cannot be undone.',
    confirmLabel: 'Delete',
    isDeleting: false,
    onConfirm: null,
  });

  const closeDeleteModal = () => {
    setDeleteModalState((prev) => ({ ...prev, show: false, isDeleting: false, onConfirm: null }));
  };
  const canSendToCurrentChat = Boolean(
    currentChat &&
      canSendMessageToRole(senderRoleId, currentChat.other_user_role_id)
  );
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [socket, setSocket] = useState(null);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [conversationFilter, setConversationFilter] = useState('all');
  const [modalSearchTerm, setModalSearchTerm] = useState("");
  const [modalRoleTab, setModalRoleTab] = useState("all");
  const [modalPracticeId, setModalPracticeId] = useState('');
  const [modalPractices, setModalPractices] = useState([]);
  const [loadingModalPractices, setLoadingModalPractices] = useState(false);

  // Profile Sidebar State
  const [showProfileInfo, setShowProfileInfo] = useState(false);
  const [profileUser, setProfileUser] = useState(null);

  // Theme State
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const [themeColor, setThemeColor] = useState('#03045e');

  // Message Filtering
  const [showMsgSearch, setShowMsgSearch] = useState(false);
  const [msgSearchTerm, setMsgSearchTerm] = useState("");

  // Emoji & Upload
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState(null);
  const [isSendingFile, setIsSendingFile] = useState(false);
  const fileInputRef = useRef(null);
  const chatInputRef = useRef(null);
  const sendLockRef = useRef(false);
  const inFlightPreviewRef = useRef(null);

  // Typing State
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef(null);

  // Patient Context Search (Live)
  const [patientSearchTerm, setPatientSearchTerm] = useState("");
  const [selectedPatientContext, setSelectedPatientContext] = useState(null);
  const [patientVitalsLoading, setPatientVitalsLoading] = useState(false);
  const [patientSearchResults, setPatientSearchResults] = useState([]);
  const [isSearchingPatients, setIsSearchingPatients] = useState(false);
  const [isSnapshotDropdownOpen, setIsSnapshotDropdownOpen] = useState(false);
  const patientSearchTimeoutRef = useRef(null);

  // Provider/Caregiver session timer when chatting with a patient
  const [chatSessionTimer, setChatSessionTimer] = useState(0);
  const [chatSessionRunning, setChatSessionRunning] = useState(false);
  const [showChatFollowUpModal, setShowChatFollowUpModal] = useState(false);
  const chatFollowUpWasRunningRef = useRef(false);

  const chatFollowUpContext = useMemo(() => {
    if (!currentUser?.id || !currentChat?.other_user_id) return null;
    const fromName = [currentUser.first_name, currentUser.last_name]
      .filter(Boolean)
      .join(' ')
      .trim() || currentUser.name || currentUser.username || 'Staff';
    return {
      fromUserId: currentUser.id,
      toUserId: currentChat.other_user_id,
      fromName,
      toName: currentChat.other_user_name || selectedPatientContext?.name || 'Patient',
    };
  }, [
    currentUser,
    currentChat?.other_user_id,
    currentChat?.other_user_name,
    selectedPatientContext?.name,
  ]);

  const [sidebarMenuOpen, setSidebarMenuOpen] = useState(false);
  const sidebarMenuRef = useRef(null);
  const [conversationSelectMode, setConversationSelectMode] = useState(false);
  const [selectedConversationIds, setSelectedConversationIds] = useState([]);
  const [openMessageMenuId, setOpenMessageMenuId] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const messageMenuRef = useRef(null);
  const [messageSelectMode, setMessageSelectMode] = useState(false);
  const [selectedMessageIds, setSelectedMessageIds] = useState([]);
  const [chatHeaderMenuOpen, setChatHeaderMenuOpen] = useState(false);
  const chatHeaderMenuRef = useRef(null);
  const [scRequiresPractice, setScRequiresPractice] = useState(false);

  const canViewPatientSnapshot = Number(currentUser?.role_id ?? reduxUser?.role_id) !== ROLES.PATIENT;
  const isProviderOrCaregiver =
    Number(senderRoleId) === ROLES.PROVIDER || Number(senderRoleId) === ROLES.CAREGIVER;
  const isChattingWithPatient =
    Boolean(currentChat) && Number(currentChat.other_user_role_id) === ROLES.PATIENT;
  const showChatPatientSessionTools =
    isProviderOrCaregiver &&
    isChattingWithPatient &&
    selectedPatientContext &&
    Number(selectedPatientContext.userId) === Number(currentChat.other_user_id) &&
    Boolean(selectedPatientContext.recordId);
  const isSystemCaregiverSender = Number(senderRoleId) === ROLES.SYSTEM_CAREGIVER;
  const isSystemAdminSender = Number(senderRoleId) === ROLES.SYSTEM_ADMIN;
  const isPracticeScopedSender = isSystemCaregiverSender || isSystemAdminSender;
  const needsPracticeAdminFilter = Number(senderRoleId) === ROLES.SUPER_ADMIN;

  const getConversationUnreadCount = (c, myId) => {
    if (!c || !myId) return 0;
    const fromApi = Number(c.unread_count);
    if (fromApi > 0) return fromApi;
    if (
      Number(c.to_user_id) === Number(myId) &&
      Number(c.from_user_id) === Number(c.other_user_id) &&
      !Number(c.is_read)
    ) {
      return 1;
    }
    return 0;
  };

  const conversationHasUnread = (c, myId) => getConversationUnreadCount(c, myId) > 0;

  const loadPatientVitalsSnapshot = async (patientUserId, meta = {}) => {
    const practiceId = currentUser?.practice_id ?? reduxUser?.practice_id;
    if (!practiceId || !patientUserId) return;
    setPatientVitalsLoading(true);
    try {
      const res = await chatService.getPatientLatestVitals(practiceId, patientUserId);
      if (res.success) {
        const recordId = res.data.id || null;
        let scheduleTargets = null;
        if (recordId) {
          try {
            const sources = await patientService.getScheduleTargetSourcesForPatient(practiceId, recordId);
            const effective = resolveEffectiveScheduleTargets(
              sources.patientData || {},
              sources.practiceData || {}
            );
            scheduleTargets = hasAnyScheduleTargets(effective) ? effective : sources.practiceData;
          } catch (targetErr) {
            console.error('Failed to load schedule targets for chat snapshot', targetErr);
          }
        }
        setSelectedPatientContext({
          name: res.data.name || meta.name,
          id: res.data.patient_id || meta.id || patientUserId,
          recordId,
          practiceId: res.data.practice_id || practiceId,
          userId: res.data.user_id || patientUserId,
          vitals: {
            bp: res.data.bp,
            glucose: res.data.glucose,
            weight: res.data.weight,
          },
          scheduleTargets,
        });
      }
    } catch (err) {
      console.error('Failed to load patient vitals', err);
      setSelectedPatientContext({
        name: meta.name,
        id: meta.id || patientUserId,
        recordId: null,
        practiceId,
        userId: patientUserId,
        vitals: { bp: null, glucose: null, weight: null },
      });
    } finally {
      setPatientVitalsLoading(false);
    }
  };

  const fetchPatientSearchResults = async (searchTerm = '') => {
    if (!currentUser?.practice_id) {
       setPatientSearchResults([]);
       return;
    }
    setIsSearchingPatients(true);
    try {
       const res = await axios.get(`/api/practices/${currentUser.practice_id}/patients?search=${encodeURIComponent(searchTerm)}`);
       if(res.data.success) {
           setPatientSearchResults(res.data.data.patients || []);
       }
    } catch(err) {
       console.error("Failed to search patients", err);
    } finally {
       setIsSearchingPatients(false);
    }
  };

  const handlePatientSearchChange = (val) => {
     setPatientSearchTerm(val);
     if (patientSearchTimeoutRef.current) clearTimeout(patientSearchTimeoutRef.current);
     setIsSnapshotDropdownOpen(true);
     patientSearchTimeoutRef.current = setTimeout(() => {
        fetchPatientSearchResults(val);
     }, 300);
  };

  const handlePatientSearchFocus = () => {
     setIsSnapshotDropdownOpen(true);
     if (patientSearchResults.length === 0) {
        fetchPatientSearchResults(patientSearchTerm);
     }
  };

  const handlePatientSearchBlur = () => {
     setTimeout(() => setIsSnapshotDropdownOpen(false), 200);
  };

  // Refs
  const messagesEndRef = useRef(null);
  const currentChatRef = useRef(null);
  const conversationsLoadedRef = useRef(false);
  const skipPendingClearRef = useRef(true);
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    currentChatRef.current = currentChat;
  }, [currentChat]);

  const clearPendingAttachment = useCallback(() => {
    setPendingFile(null);
    setPendingPreviewUrl((url) => {
      if (url) URL.revokeObjectURL(url);
      return null;
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  useEffect(() => {
    if (skipPendingClearRef.current) {
      skipPendingClearRef.current = false;
      return;
    }
    clearPendingAttachment();
  }, [currentChat?.other_user_id, clearPendingAttachment]);

  useEffect(() => {
    return () => {
      if (inFlightPreviewRef.current) URL.revokeObjectURL(inFlightPreviewRef.current);
    };
  }, []);

  const fetchConversations = useCallback(async (userId) => {
    try {
      const { data } = await axios.get(`/api/chat/conversations/${userId}`);
      if (data.success) {
        setConversations(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
    } finally {
      conversationsLoadedRef.current = true;
    }
  }, []);

  const applyIncomingChatMessage = useCallback(
    (msg, activeUserId) => {
      if (!msg) return;

      const chat = currentChatRef.current;
      if (messageBelongsToChat(msg, chat)) {
        setMessages((prev) => mergeChatMessage(prev, msg));
        requestAnimationFrame(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        });
      }

      const uid = activeUserId;
      if (
        uid != null &&
        (sameUserId(msg.to_user_id, uid) || sameUserId(msg.from_user_id, uid))
      ) {
        // Header unread badge is handled by useGlobalChatSocket; just refresh the list here
        if (sameUserId(msg.to_user_id, uid)) {
          dispatchChatUnreadRefresh();
        }
        fetchConversations(uid);
      }
    },
    [fetchConversations]
  );

  // 1. Initialize User & Socket
  useEffect(() => {
    // Prefer Redux user, fallback to local storage if Redux is empty (e.g. on hard refresh before persist rehydration)
    let activeUser = reduxUser;

    if (!activeUser) {
      const storedUserStr = localStorage.getItem('user') || localStorage.getItem('userInfo');
      if (storedUserStr) {
        try {
          activeUser = JSON.parse(storedUserStr);
        } catch (e) {
          console.error("Error parsing stored user", e);
        }
      }
    }

    if (activeUser) {
      setCurrentUser(activeUser);
      console.log("ChatSystem initialized with user:", activeUser);

      const newSocket = io(SOCKET_URL, {
        transports: ['websocket', 'polling'],
        withCredentials: true,
        reconnectionDelay: 500,
        reconnectionDelayMax: 4000
      });
      setSocket(newSocket);

      const joinRoom = () => {
        newSocket.emit('join-chat', activeUser.id);
      };

      newSocket.on('connect', joinRoom);
      // Re-join immediately if the socket is already connected (fast remount)
      if (newSocket.connected) {
        joinRoom();
      }

      newSocket.on('messages_read', (data) => {
        const chat = currentChatRef.current;
        if (chat && sameUserId(data.by_user_id, chat.other_user_id)) {
          setMessages((prev) => prev.map((m) =>
            (sameUserId(m.from_user_id, activeUser.id) ? { ...m, is_read: 1 } : m)
          ));
        }
      });

      newSocket.on('typing', (data) => {
        const chat = currentChatRef.current;
        if (chat && sameUserId(data.from_user_id, chat.other_user_id)) {
          setIsTyping(true);
        }
      });

      newSocket.on('stop_typing', (data) => {
        const chat = currentChatRef.current;
        if (chat && sameUserId(data.from_user_id, chat.other_user_id)) {
          setIsTyping(false);
        }
      });

      newSocket.on('receive_message', (msg) => {
        applyIncomingChatMessage(msg, activeUser.id);
      });

      newSocket.on('message_edited', (payload) => {
        const updated = payload?.message;
        if (!updated?.id) return;
        setMessages((prev) =>
          prev.map((m) => (Number(m.id) === Number(updated.id) ? { ...m, ...updated } : m))
        );
      });

      newSocket.on('message_deleted', (payload) => {
        const mode = payload?.mode;
        const uid = activeUser?.id;
        if (mode === 'everyone' && payload?.message?.id) {
          setMessages((prev) =>
            prev.map((m) =>
              Number(m.id) === Number(payload.message.id) ? { ...m, ...payload.message } : m
            )
          );
          return;
        }
        if (mode === 'me' && sameUserId(payload?.user_id, uid) && payload?.message_id) {
          setMessages((prev) =>
            prev.filter((m) => Number(m.id) !== Number(payload.message_id))
          );
        }
      });

      return () => {
        newSocket.off('connect', joinRoom);
        newSocket.disconnect();
      };
    } else {
      console.warn("ChatSystem: No user found in Redux or localStorage");
    }
  }, [reduxUser, applyIncomingChatMessage]);

  // Also apply messages from the app-wide chat socket (covers reconnect / missed page socket)
  useEffect(() => {
    if (!currentUser?.id) return undefined;

    const onWindowChatMessage = (event) => {
      const msg = event?.detail;
      if (!msg) return;
      // Avoid double dispatchChatMessageEvent from applyIncoming when already from global socket
      const chat = currentChatRef.current;
      if (!messageBelongsToChat(msg, chat)) {
        fetchConversations(currentUser.id);
        return;
      }
      setMessages((prev) => mergeChatMessage(prev, msg));
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      });
      fetchConversations(currentUser.id);
    };

    window.addEventListener(CHAT_MESSAGE_EVENT, onWindowChatMessage);
    return () => window.removeEventListener(CHAT_MESSAGE_EVENT, onWindowChatMessage);
  }, [currentUser?.id, fetchConversations]);

  // 2. Fetch Conversations when user is set (not on every message — that caused lag)
  useEffect(() => {
    if (currentUser) {
      conversationsLoadedRef.current = false;
      fetchConversations(currentUser.id);
    }
  }, [currentUser, fetchConversations]);

  // Open a conversation only when arriving from a notification (?with=userId).
  // Do not auto-select the first chat — the empty state stays until the user picks one.
  useEffect(() => {
    if (!currentUser?.id) return undefined;
    const withParam = searchParams.get('with');
    if (!withParam) return undefined;

    const otherId = Number(withParam);
    if (!Number.isFinite(otherId)) {
      setSearchParams({}, { replace: true });
      return undefined;
    }

    const clearDeepLink = () => {
      if (searchParams.get('with')) {
        setSearchParams({}, { replace: true });
      }
    };

    const existing = conversations.find((c) => Number(c.other_user_id) === otherId);
    if (existing) {
      setCurrentChat(existing);
      if (Number(existing.other_user_role_id) !== ROLES.PATIENT) {
        setSelectedPatientContext(null);
      }
      clearDeepLink();
      dispatchChatUnreadRefresh();
      return undefined;
    }

    if (!conversationsLoadedRef.current) {
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`/api/chat/user/${otherId}`);
        if (cancelled || !data.success) return;
        const u = data.data;
        setCurrentChat({
          other_user_id: u.id,
          other_user_name: u.name,
          other_user_avatar: u.avatar,
          other_user_role_id: u.role_id,
          other_user_role_name: u.role_name,
        });
        if (Number(u.role_id) !== ROLES.PATIENT) {
          setSelectedPatientContext(null);
        }
        dispatchChatUnreadRefresh();
      } catch (err) {
        console.error('Failed to open chat from notification link', err);
      } finally {
        if (!cancelled) clearDeepLink();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, currentUser?.id, conversations, setSearchParams]);

  useEffect(() => {
    if (!canViewPatientSnapshot) return;
    if (!currentChat || Number(currentChat.other_user_role_id) !== ROLES.PATIENT) {
      return;
    }
    loadPatientVitalsSnapshot(currentChat.other_user_id, {
      name: currentChat.other_user_name,
      id: currentChat.other_user_id,
    });
  }, [
    currentChat?.other_user_id,
    currentChat?.other_user_role_id,
    currentUser?.practice_id,
    canViewPatientSnapshot,
  ]);

  // Reset / start session timer when provider or caregiver opens a patient chat
  useEffect(() => {
    if (!showChatPatientSessionTools) {
      setChatSessionRunning(false);
      setShowChatFollowUpModal(false);
      return;
    }
    setChatSessionTimer(0);
    setChatSessionRunning(true);
    setShowChatFollowUpModal(false);
  }, [
    showChatPatientSessionTools,
    currentChat?.other_user_id,
  ]);

  useEffect(() => {
    if (!chatSessionRunning) return undefined;
    const interval = setInterval(() => {
      setChatSessionTimer((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [chatSessionRunning]);

  // 3. Fetch Messages when chat is selected
  useEffect(() => {
    if (currentChat && currentUser) {
      setEditingMessageId(null);
      setInputText('');
      setMessageSelectMode(false);
      setSelectedMessageIds([]);
      setChatHeaderMenuOpen(false);
      fetchHistory(currentUser.id, currentChat.other_user_id);
    }
  }, [currentChat?.other_user_id, currentUser?.id]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (sidebarMenuRef.current && !sidebarMenuRef.current.contains(e.target)) {
        setSidebarMenuOpen(false);
      }
      if (messageMenuRef.current && !messageMenuRef.current.contains(e.target)) {
        setOpenMessageMenuId(null);
      }
      if (chatHeaderMenuRef.current && !chatHeaderMenuRef.current.contains(e.target)) {
        setChatHeaderMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const fetchHistory = async (myId, otherId) => {
    try {
      const { data } = await axios.get(`/api/chat/history/${myId}/${otherId}`);
      if (data.success) {
        setMessages(data.data);
        scrollToBottom();
        fetchConversations(myId);
        dispatchChatUnreadRefresh();
      }
    } catch (err) {
      console.error("Failed to fetch history:", err);
    }
  };

  const scrollToBottom = (smooth = false) => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
    });
  };



  const handleInputCheck = (e) => {
    const val = e.target.value;
    setInputText(val);

    if (!canSendMessages || !socket || !currentChat || !currentUser) return;

    // Emit typing
    socket.emit('typing', {
      from_user_id: currentUser.id,
      to_user_id: currentChat.other_user_id
    });

    // Clear existing timeout
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

    // Set timeout to stop typing
    typingTimeoutRef.current = setTimeout(() => {
      socket.emit('stop_typing', {
        from_user_id: currentUser.id,
        to_user_id: currentChat.other_user_id
      });
    }, 600);
  };

  // 4. Handle Send
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!canSendMessages) {
      toast.info('You can read messages here, but your role cannot send replies.');
      return;
    }
    if (!currentUser || !currentChat) return;

    const text = inputText.trim();
    const fileToSend = pendingFile;

    if (editingMessageId) {
      if (!text) {
        toast.error('Message cannot be empty');
        return;
      }
      try {
        const res = await chatService.editMessage(editingMessageId, currentUser.id, text);
        if (res?.data) {
          setMessages((prev) =>
            prev.map((m) => (m.id === editingMessageId ? { ...m, ...res.data } : m))
          );
        }
        setEditingMessageId(null);
        setInputText('');
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to edit message');
      }
      return;
    }

    if (!text && !fileToSend) return;

    if (!canSendMessageToRole(senderRoleId, currentChat.other_user_role_id)) {
      toast.error(CHAT_SEND_BLOCKED_MESSAGE);
      return;
    }

    if (sendLockRef.current) return;
    sendLockRef.current = true;

    const tempId = `pending-${Date.now()}`;
    const optimistic = {
      id: tempId,
      from_user_id: currentUser.id,
      to_user_id: currentChat.other_user_id,
      message: text,
      created_at: new Date().toISOString(),
      is_read: 0,
      _pending: true,
      _pendingFile: Boolean(fileToSend),
      file_path: fileToSend ? pendingPreviewUrl : null,
      file_type: fileToSend ? fileToSend.type : null,
      original_file_name: fileToSend ? fileToSend.name : null,
    };

    setMessages((prev) => [...prev, optimistic]);
    setInputText('');
    if (fileToSend) {
      setIsSendingFile(true);
      inFlightPreviewRef.current = pendingPreviewUrl;
      setPendingFile(null);
      setPendingPreviewUrl(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
    scrollToBottom(false);

    try {
      let fileMeta = null;
      if (fileToSend) {
        const formData = new FormData();
        formData.append('file', fileToSend);
        const uploadRes = await axios.post('/api/chat/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (!uploadRes.data?.success) {
          throw new Error(uploadRes.data?.message || 'File upload failed');
        }
        fileMeta = uploadRes.data.data;
      }

      const payload = {
        from_user_id: currentUser.id,
        to_user_id: currentChat.other_user_id,
        message: text,
        practice_id: currentUser.practice_id,
        ...(fileMeta
          ? {
              file_path: fileMeta.file_path,
              file_type: fileMeta.file_type,
              original_file_name: fileMeta.original_file_name,
            }
          : {}),
      };

      const { data } = await axios.post('/api/chat/send', payload);

      if (data.success) {
        setMessages((prev) => {
          const withoutTemp = prev.filter((m) => m.id !== tempId);
          return mergeChatMessage(withoutTemp, data.data);
        });
        if (inFlightPreviewRef.current) {
          URL.revokeObjectURL(inFlightPreviewRef.current);
          inFlightPreviewRef.current = null;
        }
        fetchConversations(currentUser.id);
        scrollToBottom(false);
      } else {
        throw new Error(data.message || 'Failed to send message');
      }
    } catch (err) {
      console.error('Send failed:', err);
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      setInputText(text);
      if (fileToSend) {
        setPendingFile(fileToSend);
        setPendingPreviewUrl(inFlightPreviewRef.current);
        inFlightPreviewRef.current = null;
      }
      const msg =
        err.response?.data?.message ||
        (err.response?.status === 403
          ? CHAT_SEND_BLOCKED_MESSAGE
          : fileToSend
            ? 'File upload failed'
            : 'Failed to send message');
      toast.error(msg);
    } finally {
      sendLockRef.current = false;
      setIsSendingFile(false);
    }
  };

  // 5. Handle Start Chat (Modal)
  const handleEmojiClick = (emojiData) => {
    setInputText(prev => prev + emojiData.emoji);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!canSendMessages) {
      toast.info('You can read messages here, but your role cannot send attachments.');
      return;
    }
    if (!currentChat || !canSendMessageToRole(senderRoleId, currentChat.other_user_role_id)) {
      toast.error(CHAT_SEND_BLOCKED_MESSAGE);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be 10 MB or smaller.');
      return;
    }

    setPendingPreviewUrl((url) => {
      if (url) URL.revokeObjectURL(url);
      return file.type?.startsWith('image/') ? URL.createObjectURL(file) : null;
    });
    setPendingFile(file);
    requestAnimationFrame(() => {
      chatInputRef.current?.focus();
    });
  };

  const loadChatUsersForModal = async (activeUser, practiceIdOverride = null) => {
    const roleId = Number(activeUser?.role_id);
    const isPracticeScoped =
      roleId === ROLES.SYSTEM_CAREGIVER || roleId === ROLES.SYSTEM_ADMIN;
    const pId = practiceIdOverride || activeUser?.practice_id || 'null';
    const url = `/api/chat/users/${pId}/${activeUser.id}`;
    const { data } = await axios.get(url);

    if (!data?.success) {
      console.error('Fetch success false:', data);
      setAvailableUsers([]);
      return data;
    }

    setAvailableUsers(Array.isArray(data.data) ? data.data : []);

    if (isPracticeScoped) {
      const practices = Array.isArray(data.practices) ? data.practices : [];
      setModalPractices(practices);
      setScRequiresPractice(Boolean(data.requires_practice));
      if (data.practice_id) {
        setModalPracticeId(String(data.practice_id));
      } else if (practices.length === 1) {
        setModalPracticeId(String(practices[0].id));
      }
    }

    return data;
  };

  const openNewChatModal = async () => {
    if (!canSendMessages) {
      toast.info('You can read messages sent to you. Starting new conversations is not available for your role.');
      return;
    }
    // Use state currentUser, fallback to Redux user
    let activeUser = currentUser || reduxUser;

    // Last resort fallback
    if (!activeUser) {
      const stored = localStorage.getItem('user');
      if (stored) activeUser = JSON.parse(stored);
    }

    console.log("Opening chat modal with user:", activeUser);

    if (!activeUser) {
      alert("User session not found. Please reload or log in.");
      return;
    }

    setIsModalOpen(true);
    setAvailableUsers([]);
    setModalSearchTerm('');
    setModalRoleTab('all');
    setModalPracticeId('');
    setScRequiresPractice(false);
    setIsLoadingUsers(true);

    try {
      const roleId = Number(activeUser.role_id);

      if (roleId === ROLES.SYSTEM_CAREGIVER || roleId === ROLES.SYSTEM_ADMIN) {
        setLoadingModalPractices(true);
        try {
          await loadChatUsersForModal(activeUser, 'null');
        } finally {
          setLoadingModalPractices(false);
        }
      } else {
        const pId = activeUser.practice_id || 'null';
        const [{ data }, practicesResult] = await Promise.all([
          axios.get(`/api/chat/users/${pId}/${activeUser.id}`),
          needsPracticeAdminFilter
            ? (async () => {
                setLoadingModalPractices(true);
                try {
                  const response = await practiceService.getPractices({ page: 1, limit: 1000 });
                  const payload = response?.data;
                  let list = [];
                  if (payload?.success && payload?.data?.practices) {
                    list = payload.data.practices;
                  } else if (payload?.success && Array.isArray(payload?.data)) {
                    list = payload.data;
                  } else if (Array.isArray(payload?.data)) {
                    list = payload.data;
                  } else if (Array.isArray(payload)) {
                    list = payload;
                  }
                  return list;
                } catch (err) {
                  console.error('Failed to load practices for chat modal:', err);
                  return [];
                } finally {
                  setLoadingModalPractices(false);
                }
              })()
            : Promise.resolve([]),
        ]);

        if (needsPracticeAdminFilter) {
          setModalPractices(Array.isArray(practicesResult) ? practicesResult : []);
        }

        if (data.success) {
          setAvailableUsers(data.data);
        } else {
          console.error("Fetch success false:", data);
        }
      }
    } catch (err) {
      console.error("Users fetch error:", err);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleModalPracticeChange = async (nextPracticeId) => {
    setModalPracticeId(nextPracticeId);

    if (!isPracticeScopedSender && !needsPracticeAdminFilter) return;

    const activeUser = currentUser || reduxUser;
    if (!activeUser?.id) return;

    if (!nextPracticeId) {
      if (isPracticeScopedSender) {
        setAvailableUsers([]);
      } else if (needsPracticeAdminFilter) {
        setIsLoadingUsers(true);
        try {
          await loadChatUsersForModal(activeUser, 'null');
        } catch (err) {
          console.error('Failed to reload chat users:', err);
          setAvailableUsers([]);
        } finally {
          setIsLoadingUsers(false);
        }
      }
      return;
    }

    setIsLoadingUsers(true);
    try {
      await loadChatUsersForModal(activeUser, nextPracticeId);
    } catch (err) {
      console.error('Failed to reload chat users for practice:', err);
      setAvailableUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const startNewConversation = (user) => {
    if (!canSendMessageToRole(senderRoleId, user.role_id)) {
      toast.error(CHAT_SEND_BLOCKED_MESSAGE);
      return;
    }
    // metadata for the new/existing chat
    const chatObj = {
      other_user_id: user.id,
      other_user_name: user.name || user.username || user.first_name,
      other_user_avatar: user.avatar,
      other_user_role_id: user.role_id,
      other_user_role_name: user.role_name,
      other_user_practice_name: user.practice_name || null,
    };

    // Check if we already have this conversation in our list
    const existing = conversations.find(c => c.other_user_id === user.id);
    if (existing) {
      setCurrentChat(existing);
    } else {
      setCurrentChat(chatObj);
    }

    if (Number(user.role_id) !== ROLES.PATIENT) {
      setSelectedPatientContext(null);
    }

    // Reset states
    setIsTyping(false);
    setShowProfileInfo(false); // Close profile if switching chat

    setIsModalOpen(false);
  };

  const handleProfileClick = async () => {
    if (!currentChat) return;
    if (showProfileInfo) {
      setShowProfileInfo(false);
      return;
    }

    setShowProfileInfo(true);
    // Fetch fresh details
    try {
      const { data } = await axios.get(`/api/chat/user/${currentChat.other_user_id}`);
      if (data.success) {
        setProfileUser(data.data);
      }
    } catch (err) {
      console.error("Failed to fetch profile:", err);
    }
  };

  // Theme Handler
  const handleThemeChange = (color) => {
    setThemeColor(color);
    document.documentElement.style.setProperty('--primary-color', color);
    setShowThemeMenu(false);
  };

  const themeOptions = [
    '#03045e',
    '#1B2A4A',
    '#0077b6',
    '#00b4d8',
  ];

  // Helpers
  const getInitials = (name) => name ? name.charAt(0).toUpperCase() : "?";
  const getColorIndex = (name) => name ? (name.charCodeAt(0) % 6) + 1 : 1;
  const formatTime = (ts) => {
    if (!ts) return "";
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getChatDayKey = (ts) => {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  };

  const formatChatDateLabel = (ts) => {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return '';
    const today = new Date();
    const startToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const startThat = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const diffDays = Math.round((startToday - startThat) / 86400000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return d.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric'
    });
  };

  const renderTicks = (msg) => {
    if (msg.from_user_id !== currentUser?.id) return null; // Only show ticks for my messages

    // Logic: 
    // is_read = 1 -> Double Blue
    // is_read = 0 -> Single or Double Grey (Simulated "Delivered")

    if (msg.is_read) {
      return <FontAwesomeIcon icon={faCheckDouble} className="tick-icon tick-read-on-primary" title="Read" />;
    }
    return <FontAwesomeIcon icon={faCheck} className="tick-icon tick-sent" title="Sent" />;
  };

  const toggleConversationSelection = (otherUserId) => {
    const id = Number(otherUserId);
    setSelectedConversationIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const startConversationSelectMode = () => {
    setSidebarMenuOpen(false);
    setMessageSelectMode(false);
    setSelectedMessageIds([]);
    setConversationSelectMode(true);
    setSelectedConversationIds([]);
  };

  const cancelConversationSelectMode = () => {
    setConversationSelectMode(false);
    setSelectedConversationIds([]);
  };

  const handleDeleteSelectedConversations = () => {
    if (!currentUser?.id || selectedConversationIds.length === 0) return;
    const count = selectedConversationIds.length;
    setDeleteModalState({
      show: true,
      title: count === 1 ? 'Delete Conversation' : 'Delete Conversations',
      message: count === 1
        ? 'Are you sure you want to delete this conversation from your list? Messages will be removed from your view only.'
        : `Are you sure you want to delete ${count} conversation(s) from your list? Messages will be removed from your view only.`,
      warning: 'This action cannot be undone.',
      confirmLabel: 'Delete',
      isDeleting: false,
      onConfirm: confirmDeleteSelectedConversations,
    });
  };

  const confirmDeleteSelectedConversations = async () => {
    if (!currentUser?.id || selectedConversationIds.length === 0) return;
    setDeleteModalState((prev) => ({ ...prev, isDeleting: true }));
    try {
      await chatService.deleteConversations(currentUser.id, selectedConversationIds);
      if (
        currentChat &&
        selectedConversationIds.includes(Number(currentChat.other_user_id))
      ) {
        setCurrentChat(null);
        setMessages([]);
      }
      cancelConversationSelectMode();
      fetchConversations(currentUser.id);
      toast.success('Conversation(s) deleted');
      closeDeleteModal();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete conversations');
      setDeleteModalState((prev) => ({ ...prev, isDeleting: false }));
    }
  };

  const handleDeleteCurrentChat = () => {
    if (!currentUser?.id || !currentChat?.other_user_id) return;
    setChatHeaderMenuOpen(false);
    const otherUserId = Number(currentChat.other_user_id);
    const chatName = currentChat.other_user_name || 'this conversation';
    setDeleteModalState({
      show: true,
      title: 'Delete Chat',
      message: `Are you sure you want to delete your conversation with ${chatName}? Messages will be removed from your view only.`,
      warning: 'This action cannot be undone.',
      confirmLabel: 'Delete',
      isDeleting: false,
      onConfirm: () => confirmDeleteCurrentChat(otherUserId),
    });
  };

  const confirmDeleteCurrentChat = async (otherUserId) => {
    setDeleteModalState((prev) => ({ ...prev, isDeleting: true }));
    try {
      await chatService.deleteConversations(currentUser.id, [otherUserId]);
      if (currentChat && Number(currentChat.other_user_id) === otherUserId) {
        setCurrentChat(null);
        setMessages([]);
      }
      fetchConversations(currentUser.id);
      toast.success('Chat deleted');
      closeDeleteModal();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete chat');
      setDeleteModalState((prev) => ({ ...prev, isDeleting: false }));
    }
  };

  const handleDeleteMessage = (msg, modeParam = null) => {
    if (!currentUser?.id || !msg?.id) return;
    setOpenMessageMenuId(null);
    const isMe = msg.from_user_id === currentUser.id;
    const withinWindow = isMe && isWithinChatMessageActionWindow(msg.created_at);
    const canDeleteEveryone = isMe && withinWindow && !msg.deleted_for_everyone_at;

    if (modeParam === 'everyone') {
      setDeleteModalState({
        show: true,
        title: 'Delete Message for Everyone',
        message: 'Are you sure you want to delete this message for everyone in this chat?',
        warning: 'This action cannot be undone.',
        confirmLabel: 'Delete for Everyone',
        secondaryConfirmLabel: null,
        isDeleting: false,
        onConfirm: () => confirmDeleteMessage(msg, 'everyone'),
        onSecondaryConfirm: null,
      });
    } else if (modeParam === 'me') {
      setDeleteModalState({
        show: true,
        title: 'Delete Message for Me',
        message: 'Are you sure you want to delete this message for yourself?',
        warning: 'This action cannot be undone.',
        confirmLabel: 'Delete for Me',
        secondaryConfirmLabel: null,
        isDeleting: false,
        onConfirm: () => confirmDeleteMessage(msg, 'me'),
        onSecondaryConfirm: null,
      });
    } else if (canDeleteEveryone) {
      setDeleteModalState({
        show: true,
        title: 'Delete Message',
        message: 'Choose how you want to delete this message:',
        warning: 'This action cannot be undone.',
        confirmLabel: 'Delete for Everyone',
        secondaryConfirmLabel: 'Delete for Me',
        isDeleting: false,
        onConfirm: () => confirmDeleteMessage(msg, 'everyone'),
        onSecondaryConfirm: () => confirmDeleteMessage(msg, 'me'),
      });
    } else {
      setDeleteModalState({
        show: true,
        title: 'Delete Message',
        message: 'Are you sure you want to delete this message for yourself?',
        warning: 'This action cannot be undone.',
        confirmLabel: 'Delete for Me',
        secondaryConfirmLabel: null,
        isDeleting: false,
        onConfirm: () => confirmDeleteMessage(msg, 'me'),
        onSecondaryConfirm: null,
      });
    }
  };

  const confirmDeleteMessage = async (msg, mode) => {
    setDeleteModalState((prev) => ({ ...prev, isDeleting: true }));
    try {
      const res = await chatService.deleteMessage(msg.id, currentUser.id, mode);
      if (mode === 'everyone' && res?.data) {
        setMessages((prev) =>
          prev.map((m) => (m.id === msg.id ? { ...m, ...res.data } : m))
        );
      } else if (mode === 'me') {
        setMessages((prev) => prev.filter((m) => m.id !== msg.id));
      }
      fetchConversations(currentUser.id);
      toast.success(mode === 'everyone' ? 'Message deleted for everyone' : 'Message deleted for you');
      closeDeleteModal();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete message');
      setDeleteModalState((prev) => ({ ...prev, isDeleting: false }));
    }
  };

  const handleStartEditMessage = (msg) => {
    if (!msg?.id || msg.file_path) {
      toast.info('Only text messages can be edited.');
      return;
    }
    if (!isWithinChatMessageActionWindow(msg.created_at)) {
      toast.error(chatActionWindowExpiredMessage());
      return;
    }
    setOpenMessageMenuId(null);
    setEditingMessageId(msg.id);
    setInputText(msg.message || '');
    clearPendingAttachment();
    requestAnimationFrame(() => {
      chatInputRef.current?.focus();
    });
  };

  const handleCancelEditMessage = () => {
    setEditingMessageId(null);
    setInputText('');
  };

  const startMessageSelectMode = () => {
    setChatHeaderMenuOpen(false);
    setConversationSelectMode(false);
    setSelectedConversationIds([]);
    setMessageSelectMode(true);
    setSelectedMessageIds([]);
  };

  const cancelMessageSelectMode = () => {
    setMessageSelectMode(false);
    setSelectedMessageIds([]);
  };

  const toggleMessageSelection = (messageId) => {
    const id = Number(messageId);
    if (!Number.isFinite(id)) return;
    setSelectedMessageIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectedObjects = messages.filter((m) =>
    selectedMessageIds.includes(Number(m.id))
  );

  const canDeleteSelectedForEveryone =
    selectedObjects.length > 0 &&
    selectedObjects.every(
      (m) =>
        m.from_user_id === currentUser?.id &&
        !m.deleted_for_everyone_at &&
        isWithinChatMessageActionWindow(m.created_at)
    );

  const handleDeleteSelectedMessages = (modeParam = null) => {
    if (!currentUser?.id || selectedMessageIds.length === 0) return;
    const count = selectedMessageIds.length;

    if (modeParam === 'everyone') {
      setDeleteModalState({
        show: true,
        title: 'Delete Messages for Everyone',
        message: `Are you sure you want to delete ${count} message(s) for everyone in this chat?`,
        warning: 'This action cannot be undone.',
        confirmLabel: 'Delete for Everyone',
        secondaryConfirmLabel: null,
        isDeleting: false,
        onConfirm: () => confirmDeleteSelectedMessages('everyone'),
        onSecondaryConfirm: null,
      });
    } else if (modeParam === 'me') {
      setDeleteModalState({
        show: true,
        title: 'Delete Messages for Me',
        message: `Are you sure you want to delete ${count} message(s) from your view?`,
        warning: 'This action cannot be undone.',
        confirmLabel: 'Delete for Me',
        secondaryConfirmLabel: null,
        isDeleting: false,
        onConfirm: () => confirmDeleteSelectedMessages('me'),
        onSecondaryConfirm: null,
      });
    } else if (canDeleteSelectedForEveryone) {
      setDeleteModalState({
        show: true,
        title: 'Delete Messages',
        message: `Choose how you want to delete the ${count} selected message(s):`,
        warning: 'This action cannot be undone.',
        confirmLabel: 'Delete for Everyone',
        secondaryConfirmLabel: 'Delete for Me',
        isDeleting: false,
        onConfirm: () => confirmDeleteSelectedMessages('everyone'),
        onSecondaryConfirm: () => confirmDeleteSelectedMessages('me'),
      });
    } else {
      setDeleteModalState({
        show: true,
        title: 'Delete Messages',
        message: `Are you sure you want to delete ${count} message(s) from your view?`,
        warning: 'This action cannot be undone.',
        confirmLabel: 'Delete for Me',
        secondaryConfirmLabel: null,
        isDeleting: false,
        onConfirm: () => confirmDeleteSelectedMessages('me'),
        onSecondaryConfirm: null,
      });
    }
  };

  const confirmDeleteSelectedMessages = async (mode = 'me') => {
    if (!currentUser?.id || selectedMessageIds.length === 0) return;
    setDeleteModalState((prev) => ({ ...prev, isDeleting: true }));
    try {
      const resList = await Promise.all(
        selectedMessageIds.map((id) =>
          chatService.deleteMessage(id, currentUser.id, mode)
        )
      );
      if (mode === 'everyone') {
        const updatedMsgMap = new Map();
        resList.forEach((r) => {
          if (r?.data?.id) {
            updatedMsgMap.set(Number(r.data.id), r.data);
          }
        });
        setMessages((prev) =>
          prev.map((m) => {
            const updated = updatedMsgMap.get(Number(m.id));
            return updated ? { ...m, ...updated } : m;
          })
        );
      } else {
        setMessages((prev) =>
          prev.filter((m) => !selectedMessageIds.includes(Number(m.id)))
        );
      }
      cancelMessageSelectMode();
      fetchConversations(currentUser.id);
      toast.success(
        mode === 'everyone'
          ? 'Message(s) deleted for everyone'
          : 'Message(s) deleted for you'
      );
      closeDeleteModal();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete messages');
      setDeleteModalState((prev) => ({ ...prev, isDeleting: false }));
    }
  };

  const handleMessageRowClick = (msg) => {
    if (!messageSelectMode || !msg?.id || msg.deleted_for_everyone_at) return;
    toggleMessageSelection(msg.id);
  };

  const handleSelectConversation = (c) => {
    if (conversationSelectMode) {
      toggleConversationSelection(c.other_user_id);
      return;
    }
    setCurrentChat(c);
    if (Number(c.other_user_role_id) !== ROLES.PATIENT) {
      setSelectedPatientContext(null);
    }
  };

  const renderMessageActionsMenu = (msg, isMe) => {
    if (messageSelectMode) return null;
    const withinWindow = isMe && isWithinChatMessageActionWindow(msg.created_at);
    const canEdit = isMe && withinWindow && !msg.deleted_for_everyone_at && !msg.file_path;
    const canDeleteEveryone =
      isMe && withinWindow && !msg.deleted_for_everyone_at;
    const menuOpen = openMessageMenuId === msg.id;

    return (
      <div
        className={`message-actions-wrap ${menuOpen ? 'is-open' : ''}`}
        ref={menuOpen ? messageMenuRef : null}
      >
        <button
          type="button"
          className="message-actions-btn"
          aria-label="Message options"
          aria-expanded={menuOpen}
          onClick={(e) => {
            e.stopPropagation();
            setOpenMessageMenuId(menuOpen ? null : msg.id);
          }}
        >
          <FontAwesomeIcon icon={faChevronDown} />
        </button>
        {menuOpen && (
          <div className="message-actions-menu" role="menu">
            {canEdit && (
              <button type="button" role="menuitem" onClick={() => handleStartEditMessage(msg)}>
                Edit
              </button>
            )}
            <button type="button" role="menuitem" onClick={() => handleDeleteMessage(msg, 'me')}>
              Delete for me
            </button>
            {canDeleteEveryone && (
              <button
                type="button"
                role="menuitem"
                className="danger"
                onClick={() => handleDeleteMessage(msg, 'everyone')}
              >
                Delete for everyone
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Filtered lists
  const filteredConvos = conversations.filter(c => {
    const matchesSearch = matchesWordStart(c.other_user_name, searchTerm);
    if (!matchesSearch) return false;
    if (conversationFilter === 'unread') {
      return conversationHasUnread(c, currentUser?.id);
    }
    return true;
  });

  const totalUnreadMessageCount = conversations.reduce(
    (sum, c) => sum + getConversationUnreadCount(c, currentUser?.id),
    0
  );

  const matchesModalSearch = (u) => {
    return matchesWordStartAny(
      [u.name || u.username, getChatRoleDisplay(u.role_id, u.role_name), u.username, u.practice_name],
      modalSearchTerm
    );
  };

  // Practice filter applies ONLY to Practice Admin recipients for Super/System Admin.
  // System Caregiver lists are already practice-scoped by the API.
  const filteredAvailableUsers = availableUsers.filter((u) => {
    if (!matchesModalSearch(u)) return false;
    const isPracticeAdmin = Number(u.role_id) === ROLES.PRACTICE_ADMIN;
    if (needsPracticeAdminFilter && isPracticeAdmin) {
      if (!modalPracticeId) return false;
      const ids = parsePracticeIds(u.practice_ids || u.practice_id);
      if (ids.length > 0) {
        return ids.includes(Number(modalPracticeId));
      }
      return true;
    }
    return true;
  });

  const superAdminUsers = availableUsers.filter(
    (u) => Number(u.role_id) === ROLES.SUPER_ADMIN && matchesModalSearch(u)
  );
  const systemAdminUsers = availableUsers.filter(
    (u) => Number(u.role_id) === ROLES.SYSTEM_ADMIN && matchesModalSearch(u)
  );
  const systemCaregiverUsers = availableUsers.filter(
    (u) => Number(u.role_id) === ROLES.SYSTEM_CAREGIVER && matchesModalSearch(u)
  );
  const practiceAdminUsers = filteredAvailableUsers.filter(
    (u) => Number(u.role_id) === ROLES.PRACTICE_ADMIN
  );
  const providerUsers = filteredAvailableUsers.filter(
    (u) => Number(u.role_id) === ROLES.PROVIDER
  );
  const caregiverUsers = filteredAvailableUsers.filter(
    (u) => Number(u.role_id) === ROLES.CAREGIVER
  );
  const patientUsers = filteredAvailableUsers.filter(
    (u) => Number(u.role_id) === ROLES.PATIENT
  );
  const otherVisibleUsers = filteredAvailableUsers.filter((u) => {
    const roleId = Number(u.role_id);
    return (
      roleId !== ROLES.SUPER_ADMIN &&
      roleId !== ROLES.SYSTEM_ADMIN &&
      roleId !== ROLES.SYSTEM_CAREGIVER &&
      roleId !== ROLES.PRACTICE_ADMIN &&
      roleId !== ROLES.PROVIDER &&
      roleId !== ROLES.CAREGIVER &&
      roleId !== ROLES.PATIENT
    );
  });

  const modalRoleTabs = isSystemAdminSender
    ? [
        {
          id: 'all',
          label: 'All',
          count: filteredAvailableUsers.length,
          roleId: null,
        },
        {
          id: String(ROLES.SUPER_ADMIN),
          label: 'Super',
          count: superAdminUsers.length,
          roleId: ROLES.SUPER_ADMIN,
        },
        {
          id: String(ROLES.SYSTEM_ADMIN),
          label: 'Sys Admin',
          count: systemAdminUsers.length,
          roleId: ROLES.SYSTEM_ADMIN,
        },
        {
          id: String(ROLES.PRACTICE_ADMIN),
          label: 'Practice Admins',
          count: practiceAdminUsers.length,
          roleId: ROLES.PRACTICE_ADMIN,
        },
        {
          id: String(ROLES.SYSTEM_CAREGIVER),
          label: 'System Care',
          count: systemCaregiverUsers.length,
          roleId: ROLES.SYSTEM_CAREGIVER,
        },
        {
          id: String(ROLES.PROVIDER),
          label: 'Providers',
          count: providerUsers.length,
          roleId: ROLES.PROVIDER,
        },
        {
          id: String(ROLES.CAREGIVER),
          label: 'Caregivers',
          count: caregiverUsers.length,
          roleId: ROLES.CAREGIVER,
        },
      ]
    : isSystemCaregiverSender
    ? [
        {
          id: 'all',
          label: 'All',
          count: filteredAvailableUsers.length,
          roleId: null,
        },
        {
          id: String(ROLES.SUPER_ADMIN),
          label: 'Super',
          count: superAdminUsers.length,
          roleId: ROLES.SUPER_ADMIN,
        },
        {
          id: String(ROLES.SYSTEM_ADMIN),
          label: 'Sys Admin',
          count: systemAdminUsers.length,
          roleId: ROLES.SYSTEM_ADMIN,
        },
        {
          id: String(ROLES.PRACTICE_ADMIN),
          label: 'Practice Admins',
          count: practiceAdminUsers.length,
          roleId: ROLES.PRACTICE_ADMIN,
        },
        {
          id: String(ROLES.PATIENT),
          label: 'Patients',
          count: patientUsers.length,
          roleId: ROLES.PATIENT,
        },
      ]
    : needsPracticeAdminFilter
    ? [
        {
          id: 'all',
          label: 'All',
          count: filteredAvailableUsers.length,
          roleId: null,
        },
        {
          id: String(ROLES.SYSTEM_ADMIN),
          label: 'Sys Admin',
          count: systemAdminUsers.length,
          roleId: ROLES.SYSTEM_ADMIN,
        },
        {
          id: String(ROLES.PRACTICE_ADMIN),
          label: 'Practice Admins',
          count: practiceAdminUsers.length,
          roleId: ROLES.PRACTICE_ADMIN,
        },
        {
          id: String(ROLES.SYSTEM_CAREGIVER),
          label: 'System Care',
          count: systemCaregiverUsers.length,
          roleId: ROLES.SYSTEM_CAREGIVER,
        },
        ...(otherVisibleUsers.length
          ? [{
              id: 'other',
              label: 'Other',
              count: otherVisibleUsers.length,
              roleId: null,
            }]
          : []),
      ]
    : buildChatRecipientRoleTabs(filteredAvailableUsers);

  const tabFilteredAvailableUsers = filteredAvailableUsers.filter((u) => {
    if (modalRoleTab === 'all') return true;
    if (modalRoleTab === 'other') {
      const roleId = Number(u.role_id);
      return (
        roleId !== ROLES.SUPER_ADMIN &&
        roleId !== ROLES.SYSTEM_ADMIN &&
        roleId !== ROLES.SYSTEM_CAREGIVER &&
        roleId !== ROLES.PRACTICE_ADMIN &&
        roleId !== ROLES.PROVIDER &&
        roleId !== ROLES.CAREGIVER &&
        roleId !== ROLES.PATIENT
      );
    }
    return String(u.role_id) === modalRoleTab;
  });

  const isPracticeAdminTab = String(modalRoleTab) === String(ROLES.PRACTICE_ADMIN);
  const showPracticeFilter =
    (needsPracticeAdminFilter && isPracticeAdminTab) ||
    (isPracticeScopedSender && (scRequiresPractice || modalPractices.length > 1));
  const waitingForScPractice =
    isPracticeScopedSender && scRequiresPractice && !modalPracticeId;

  const renderModalUserRow = (u) => {
    const roleId = Number(u.role_id);
    const isSystemAdminOrCaregiver =
      roleId === ROLES.SYSTEM_ADMIN || roleId === ROLES.SYSTEM_CAREGIVER;
    const practiceNames = parsePracticeNames(u.practice_name);
    // System Admin / System Caregiver: always show full practice names.
    // Practice Admin: show the selected practice name (or full list if multiple).
    const practiceSummary = formatPracticeSummary(u.practice_name, {
      full: isSystemAdminOrCaregiver,
    });
    const showPracticeHover = isSystemAdminOrCaregiver && practiceNames.length > 1;

    return (
      <button key={u.id} type="button" className="modal-user-item" onClick={() => startNewConversation(u)}>
        <div className={`avatar avatar-${getColorIndex(u.name || u.username)}`}>
          {getInitials(u.name || u.username)}
        </div>
        <div className="modal-user-info">
          <div className="modal-user-name-row">
            <span className="modal-user-name">{u.name || u.username}</span>
            <ChatRoleBadge roleId={u.role_id} roleName={u.role_name} />
          </div>
          <div className="modal-user-meta">
            {u.username ? `@${u.username}` : null}
            {u.username && practiceSummary ? ' · ' : null}
            {practiceSummary ? (
              <span
                className={`modal-user-practice${showPracticeHover ? ' has-practice-tooltip' : ''}`}
                title={showPracticeHover ? practiceNames.join('\n') : undefined}
              >
                {practiceSummary}
              </span>
            ) : null}
          </div>
          {showPracticeHover && (
            <div className="modal-user-practice-tooltip" role="tooltip">
              <div className="modal-user-practice-tooltip-title">Assigned practices</div>
              <ul>
                {practiceNames.map((name) => (
                  <li key={name}>{name}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </button>
    );
  };

  // -- RENDER --
  return (
    <div className="chat-wrapper">

      {/* 1. LEFT SIDEBAR: Messages / Staff List */}
      <div className="chat-sidebar">
        <div className={`sidebar-header ${conversationSelectMode ? 'sidebar-header--select-mode' : ''}`}>
          <span className="sidebar-title">
            {conversationSelectMode ? 'Select chats' : 'Messages'}
          </span>
          <div className="sidebar-header-actions" ref={sidebarMenuRef}>
            {conversationSelectMode ? (
              <>
                <button
                  type="button"
                  className="sidebar-text-btn"
                  onClick={cancelConversationSelectMode}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="sidebar-text-btn danger"
                  disabled={selectedConversationIds.length === 0}
                  onClick={handleDeleteSelectedConversations}
                >
                  Delete ({selectedConversationIds.length})
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn-icon sidebar-menu-btn"
                  aria-label="Messages options"
                  aria-expanded={sidebarMenuOpen}
                  onClick={() => setSidebarMenuOpen((o) => !o)}
                >
                  <FontAwesomeIcon icon={faEllipsisV} />
                </button>
                {sidebarMenuOpen && (
                  <div className="sidebar-dropdown-menu" role="menu">
                    <button
                      type="button"
                      role="menuitem"
                      onClick={startConversationSelectMode}
                    >
                      Select chats
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="sidebar-search">
          <SearchBar 
            value={searchTerm}
            onChange={(val) => setSearchTerm(val)}
            placeholder="Search conversations"
            label=""
            className="chat-sidebar-search"
          />
          
          <div className="sidebar-filters">
            <button
              type="button"
              className={`filter-item ${conversationFilter === 'all' ? 'active' : ''}`}
              onClick={() => setConversationFilter('all')}
            >
              All
            </button>
            <span className="filter-divider">|</span>
            <button
              type="button"
              className={`filter-item ${conversationFilter === 'unread' ? 'active' : ''}`}
              onClick={() => setConversationFilter('unread')}
            >
              Unread
              {totalUnreadMessageCount > 0 && (
                <span className="filter-unread-count">
                  {totalUnreadMessageCount > 99 ? '99+' : totalUnreadMessageCount}
                </span>
              )}
            </button>
          </div>
        </div>

        <div className="conversation-list">
          {filteredConvos.map((c) => {
            const unreadCount = getConversationUnreadCount(c, currentUser?.id);
            const unread = unreadCount > 0;
            const isSelected = selectedConversationIds.includes(Number(c.other_user_id));
            return (
              <div
                key={c.id || c.other_user_id}
                className={`conversation-item ${conversationSelectMode ? 'conversation-item--selecting' : ''} ${!conversationSelectMode && currentChat?.other_user_id === c.other_user_id ? 'active' : ''} ${unread ? 'has-unread' : ''} ${conversationSelectMode && isSelected ? 'select-mode-selected' : ''}`}
                onClick={() => handleSelectConversation(c)}
              >
                {conversationSelectMode && (
                  <div className="conv-select-cell">
                    <input
                      type="checkbox"
                      className="conv-select-checkbox"
                      checked={isSelected}
                      readOnly
                      tabIndex={-1}
                      aria-label={`Select ${c.other_user_name}`}
                    />
                  </div>
                )}
                <div className="avatar chat-user-avatar">
                  {c.other_user_avatar ? <img src={c.other_user_avatar} alt="av" style={{ width: '100%', borderRadius: '50%' }} /> : getInitials(c.other_user_name)}
                </div>
                <div className="conv-info">
                  <div className="conv-top">
                    <span className="conv-name">
                      {c.other_user_name}
                    </span>
                    <div className="conv-badges">
                      <ChatRoleBadge
                        roleId={c.other_user_role_id}
                        roleName={c.other_user_role_name}
                        className="chat-role-badge--compact"
                      />
                      {unread && (
                        <span className="conv-unread-badge" title="Unread messages">
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      )}
                    </div>
                  </div>
                  {c.other_user_practice_name && (
                    <div className="conv-practice">{c.other_user_practice_name}</div>
                  )}
                  <div className="conv-preview">
                    {c.message || 'No messages yet'}
                  </div>
                </div>
              </div>
            );
          })}
          {filteredConvos.length === 0 && (
            <div style={{ padding: 20, textAlign: 'center', color: '#888' }}>
              No conversations found
            </div>
          )}
        </div>

        {canSendMessages && !conversationSelectMode && (
          <button className="btn-start-chat-text" onClick={openNewChatModal} type="button">
            <FontAwesomeIcon icon={faPlus} />
          </button>
        )}
      </div>

      {/* 2. CHAT MAIN: Chat Conversation */}
      <div className={`chat-main`}>
        {currentChat ? (
          <>
            <div className="chat-header">
              <div
                className="chat-header-user"
                onClick={handleProfileClick}
                onKeyDown={(e) => e.key === 'Enter' && handleProfileClick()}
                role="button"
                tabIndex={0}
              >
                <div className="avatar chat-user-avatar chat-header-avatar">
                  {getInitials(currentChat.other_user_name)}
                </div>
                <div className="chat-header-user-text">
                  <span className="chat-header-name">{currentChat.other_user_name}</span>
                  <ChatRoleBadge
                    roleId={currentChat.other_user_role_id}
                    roleName={currentChat.other_user_role_name}
                    className="chat-role-badge--compact chat-header-role-badge"
                  />
                  {currentChat.other_user_practice_name && (
                    <span className="chat-header-practice">{currentChat.other_user_practice_name}</span>
                  )}
                </div>
              </div>

              <div className="chat-header-slot chat-header-slot--right">
                {messageSelectMode ? (
                  <div className="chat-header-select-actions">
                    <button type="button" className="chat-header-text-btn" onClick={cancelMessageSelectMode}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="chat-header-text-btn"
                      disabled={selectedMessageIds.length === 0}
                      onClick={() => handleDeleteSelectedMessages('me')}
                    >
                      Delete for me ({selectedMessageIds.length})
                    </button>
                    {canDeleteSelectedForEveryone && (
                      <button
                        type="button"
                        className="chat-header-text-btn chat-header-text-btn--danger"
                        disabled={selectedMessageIds.length === 0}
                        onClick={() => handleDeleteSelectedMessages('everyone')}
                      >
                        Delete for everyone ({selectedMessageIds.length})
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="chat-header-menu-wrap" ref={chatHeaderMenuRef}>
                    <button
                      type="button"
                      className="chat-header-menu-btn"
                      aria-label="Chat options"
                      aria-expanded={chatHeaderMenuOpen}
                      onClick={() => setChatHeaderMenuOpen((o) => !o)}
                    >
                      <FontAwesomeIcon icon={faEllipsisV} />
                    </button>
                    {chatHeaderMenuOpen && (
                      <div className="chat-header-dropdown" role="menu">
                        <button type="button" role="menuitem" onClick={startMessageSelectMode}>
                          Select messages
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          className="danger"
                          onClick={handleDeleteCurrentChat}
                        >
                          Delete chat
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {messageSelectMode && (
              <div className="chat-message-select-hint">
                Tap messages to select, then delete. Use the ▼ on a message for edit or single delete.
              </div>
            )}

            <div className="message-list">
              {messages
                .filter(m => {
                  const isChatMatch = (m.from_user_id === currentUser?.id && m.to_user_id === currentChat.other_user_id) ||
                    (m.from_user_id === currentChat.other_user_id && m.to_user_id === currentUser?.id);
                  if (!isChatMatch) return false;
                  if (!msgSearchTerm) return true;
                  return matchesWordStart(m.message, msgSearchTerm);
                })
                .map((msg, index, visibleMessages) => {
                  const isMe = msg.from_user_id === currentUser?.id;
                  const isDeletedForEveryone = Boolean(msg.deleted_for_everyone_at);
                  const isEditingTarget = editingMessageId === msg.id;
                  const msgSelected = selectedMessageIds.includes(Number(msg.id));
                  const canPickMessage = messageSelectMode && msg.id && !isDeletedForEveryone;
                  const prevMsg = visibleMessages[index - 1];
                  const showDateSeparator =
                    !prevMsg || getChatDayKey(prevMsg.created_at) !== getChatDayKey(msg.created_at);

                  return (
                    <React.Fragment key={msg.id || `${msg.created_at}-${msg.from_user_id}`}>
                      {showDateSeparator && (
                        <div className="chat-date-separator" role="separator">
                          <span>{formatChatDateLabel(msg.created_at)}</span>
                        </div>
                      )}
                    <div
                      className={`message-row ${isMe ? 'sent' : 'received'} ${isEditingTarget ? 'message-row--editing-target' : ''} ${canPickMessage ? 'message-row--selectable' : ''} ${msgSelected ? 'message-row--selected' : ''}`}
                      onClick={(e) => {
                        if (messageSelectMode) {
                          handleMessageRowClick(msg);
                          return;
                        }
                      }}
                    >
                      {canPickMessage && (
                        <input
                          type="checkbox"
                          className="message-select-checkbox"
                          checked={msgSelected}
                          readOnly
                          tabIndex={-1}
                          aria-label="Select message"
                        />
                      )}
                      <div
                        className={`message-bubble ${isDeletedForEveryone ? 'message-bubble--deleted' : ''}`}
                        style={{ padding: msg.file_path && !isDeletedForEveryone ? '4px 4px 6px' : undefined }}
                      >
                        {isDeletedForEveryone ? (
                          <div className="message-deleted-text">
                            <FontAwesomeIcon icon={faTrash} /> This message was deleted
                          </div>
                        ) : (
                          <>
                            {msg.file_path && (
                              <div className="msg-attachment" style={{ marginBottom: msg.message ? 8 : 0 }}>
                                {msg.file_type?.startsWith('image') ? (
                                  <a
                                    href={msg._pending ? msg.file_path : resolveChatAttachmentUrl(msg.file_path)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <img
                                      src={msg._pending ? msg.file_path : resolveChatAttachmentUrl(msg.file_path)}
                                      alt={msg.original_file_name || 'attachment'}
                                      style={{ maxWidth: '200px', borderRadius: '8px', cursor: 'pointer' }}
                                    />
                                  </a>
                                ) : (
                                  <a
                                    href={msg._pending ? undefined : resolveChatAttachmentUrl(msg.file_path)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (msg._pending) e.preventDefault();
                                    }}
                                    style={{ color: 'inherit', textDecoration: 'underline' }}
                                  >
                                    📎 {msg.original_file_name || 'Attachment'}
                                  </a>
                                )}
                              </div>
                            )}
                            {msg.message && (
                              <div className="message-bubble-text">{msg.message}</div>
                            )}
                          </>
                        )}
                        {!isDeletedForEveryone && (
                          <div className="message-bubble-footer">
                            <span className="msg-time-bubble">
                              {formatTime(msg.created_at)}
                              {isMessageEdited(msg) && (
                                <span className="msg-edited-tag"> · edited</span>
                              )}
                            </span>
                            {isMe && (
                              <span className="msg-ticks-bubble">{renderTicks(msg)}</span>
                            )}
                            {renderMessageActionsMenu(msg, isMe)}
                          </div>
                        )}
                      </div>
                    </div>
                    </React.Fragment>
                  );
                })}
              <div ref={messagesEndRef} />
            </div>

            <div style={{ position: 'relative' }}>
              {showEmojiPicker && (
                <div style={{ position: 'absolute', bottom: '80px', left: '20px', zIndex: 100 }}>
                  <div className="emoji-picker-container">
                    <EmojiPicker onEmojiClick={handleEmojiClick} />
                  </div>
                </div>
              )}
            </div>

            <div className="chat-input-area-wrapper">
              {!canSendMessages ? (
                <div className="chat-readonly-notice" role="status">
                  You can view messages from your care team. Sending replies is not available for patient accounts.
                </div>
              ) : !canSendToCurrentChat ? (
                <div className="chat-readonly-notice chat-send-blocked-notice" role="alert">
                  {CHAT_SEND_BLOCKED_MESSAGE}
                </div>
              ) : (
               <>
               {editingMessageId && (
                 <div className="chat-edit-banner">
                   <span>Editing message</span>
                   <button type="button" className="chat-edit-banner-cancel" onClick={handleCancelEditMessage}>
                     Cancel
                   </button>
                 </div>
               )}
               {pendingFile && (
                 <div className="chat-pending-file" role="status">
                   {pendingPreviewUrl ? (
                     <img src={pendingPreviewUrl} alt="" className="chat-pending-file-thumb" />
                   ) : (
                     <span className="chat-pending-file-icon" aria-hidden="true">
                       <FontAwesomeIcon icon={faPaperclip} />
                     </span>
                   )}
                   <div className="chat-pending-file-meta">
                     <span className="chat-pending-file-name">{pendingFile.name}</span>
                     <span className="chat-pending-file-size">{formatChatFileSize(pendingFile.size)} · Click Send to confirm</span>
                   </div>
                   <button
                     type="button"
                     className="chat-pending-file-remove"
                     onClick={clearPendingAttachment}
                     disabled={isSendingFile}
                     aria-label="Remove attachment"
                   >
                     <FontAwesomeIcon icon={faTimes} />
                   </button>
                 </div>
               )}
               <form className="chat-input-container" onSubmit={handleSendMessage}>
                 <input
                   type="file"
                   ref={fileInputRef}
                   style={{ display: 'none' }}
                   onChange={handleFileSelect}
                   accept="image/*,.pdf,.doc,.docx"
                   disabled={Boolean(editingMessageId) || isSendingFile}
                 />
                 <button
                   type="button"
                   className="btn-attachment"
                   title="Attach file"
                   onClick={() => fileInputRef.current?.click()}
                   disabled={Boolean(editingMessageId) || isSendingFile}
                 >
                    <FontAwesomeIcon icon={faPaperclip} />
                 </button>
                 <div className="input-field-wrapper">
                   <input
                     ref={chatInputRef}
                     className="input-field"
                     placeholder={
                       editingMessageId
                         ? 'Edit your message…'
                         : pendingFile
                           ? 'Add a caption (optional)...'
                           : 'Type a message...'
                     }
                     value={inputText}
                     onChange={handleInputCheck}
                     onKeyDown={(e) => {
                       if (e.key === 'Escape' && editingMessageId) handleCancelEditMessage();
                     }}
                     disabled={isSendingFile}
                   />
                 </div>
                 <button
                   type="submit"
                   className="btn-send-primary"
                   disabled={(!inputText.trim() && !pendingFile) || isSendingFile}
                 >
                   {editingMessageId ? 'Save' : isSendingFile ? 'Sending…' : 'Send'}
                 </button>
               </form>
               </>
              )}
            </div>
          </>
        ) : (
          <div className="chat-empty-state">
            <div className="empty-state-icon-wrapper">
              <FontAwesomeIcon icon={faPaperPlane} />
            </div>
            <h2 className="empty-state-title">Your Messages</h2>
            <p className="empty-state-desc">
              {canSendMessages
                ? <>Select a contact from the list<br/>or start a new conversation to collaborate.</>
                : <>Select a conversation on the left to read messages from your care team.</>}
            </p>
          </div>
        )}
      </div>

      {/* 3. RIGHT SIDEBAR: Focused Vital Snapshot (staff only) */}
      {canViewPatientSnapshot && (
      <div className="patient-snapshot">
         <div className="snapshot-header">
            <h3>Focused Vital Snapshot</h3>
         </div>
         
         <div className="patient-search-wrapper" style={{ position: 'relative' }}>
            <SearchBar 
              value={patientSearchTerm}
              onChange={handlePatientSearchChange}
              onFocus={handlePatientSearchFocus}
              onBlur={handlePatientSearchBlur}
              placeholder="Search patients by name or ID..."
              label=""
              className="chat-snapshot-search"
            />

            {/* Live Search Dropdown */}
            {isSnapshotDropdownOpen && (patientSearchResults.length > 0 || isSearchingPatients) && (
               <div className="patient-search-dropdown" style={{
                   position: 'absolute', top: '100%', left: 0, right: 0, 
                   background: '#ffffff', border: '1px solid var(--border-color)', 
                   borderRadius: '0 0 8px 8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                   maxHeight: '250px', overflowY: 'auto', zIndex: 100
               }}>
                  {isSearchingPatients ? (
                     <div style={{padding: '12px 14px', color: 'var(--text-secondary)', fontSize: '13px', textAlign: 'center'}}>
                        Searching...
                     </div>
                  ) : (
                     patientSearchResults.map(p => (
                        <div key={p.id} 
                             style={{padding: '10px 14px', borderBottom: '1px solid #f7fafc', cursor: 'pointer', display: 'flex', flexDirection: 'column', transition: 'background 0.2s'}}
                             onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                             onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                             onClick={() => {
                                const patientUserId = p.user_id || p.userId || p.id;
                                loadPatientVitalsSnapshot(patientUserId, {
                                   name: `${p.first_name} ${p.last_name}`,
                                   id: p.patient_id || p.id,
                                });
                                setPatientSearchTerm('');
                                setPatientSearchResults([]);
                             }}>
                             <span style={{fontWeight: 600, fontSize: '13.5px', color: 'var(--text-primary)'}}>{p.first_name} {p.last_name}</span>
                             <span style={{fontSize: '11px', color: 'var(--sidebar-text-muted)'}}>ID: {p.patient_id || p.id}</span>
                        </div>
                     ))
                  )}
               </div>
            )}
         </div>

         {patientVitalsLoading ? (
            <div className="snapshot-empty">
               <div className="snapshot-empty-container">
                  <p>Loading vitals…</p>
               </div>
            </div>
         ) : selectedPatientContext ? (() => {
            const contextData = selectedPatientContext;
            const vitals = contextData.vitals || {};
            const statuses = getSnapshotVitalStatuses(vitals, contextData.scheduleTargets);
            const sysValue = roundSnapshotReading(vitals.bp?.systolic);
            const diaValue = roundSnapshotReading(vitals.bp?.diastolic);
            const glucoseValue = vitals.glucose?.display ?? '—';
            const weightValue = vitals.weight?.display ?? '—';
            const hasAnyVital = vitals.bp || vitals.glucose || vitals.weight;
            return (
              <div className="snapshot-content">
                 <div className="concise-vital-card">
                    <div className="cvc-user">
                       <div className={`avatar avatar-${getColorIndex(contextData.name)}`} style={{width: 36, height: 36, fontSize: 14}}>
                         {getInitials(contextData.name)}
                       </div>
                       <div className="cvc-user-info">
                          <span className="cvc-name">{contextData.name}</span>
                          <span className="cvc-id">ID: {contextData.id}</span>
                       </div>
                    </div>

                    <div className="cvc-divider cvc-divider--horizontal" />

                    <div className="cvc-vitals">
                       <div className="cvc-vital-col">
                          <span className="cvc-label">BP</span>
                          <span className="cvc-value">
                            {vitals.bp && sysValue != null && diaValue != null ? (
                              <>
                                <span className={snapshotAlertClass(statuses.bp?.sysStatus)} style={snapshotReadingStyle(statuses.bp?.sysStatus)}>{sysValue}</span>
                                <span className="cvc-value-sep">/</span>
                                <span className={snapshotAlertClass(statuses.bp?.diaStatus)} style={snapshotReadingStyle(statuses.bp?.diaStatus)}>{diaValue}</span>
                              </>
                            ) : (vitals.bp?.display ?? '—')}
                          </span>
                       </div>
                       <div className="cvc-vital-col">
                          <span className="cvc-label">Glucose</span>
                          <span
                            className={`cvc-value ${vitals.glucose ? snapshotAlertClass(statuses.glucose) : ''}`}
                            style={vitals.glucose ? snapshotReadingStyle(statuses.glucose) : undefined}
                          >
                            {glucoseValue}
                          </span>
                       </div>
                       <div className="cvc-vital-col">
                          <span className="cvc-label">Weight</span>
                          <span
                            className={`cvc-value ${vitals.weight ? snapshotAlertClass(statuses.weight) : ''}`}
                            style={vitals.weight ? snapshotReadingStyle(statuses.weight) : undefined}
                          >
                            {weightValue}
                          </span>
                       </div>
                    </div>
                    {!hasAnyVital && (
                      <p className="snapshot-no-vitals">No BP, glucose, or weight readings on file yet.</p>
                    )}
                 </div>

                 {showChatPatientSessionTools && (
                   <div className="chat-session-panel" data-export-ignore="true">
                     <div className="chat-session-timer-block">
                       <div className="chat-session-timer-text">
                         <span className="chat-session-timer-label">Active Session</span>
                         <span className="chat-session-timer-value">
                           {formatChatSessionTimer(chatSessionTimer)}
                         </span>
                       </div>
                       <div className="chat-session-timer-actions">
                         <button
                           type="button"
                           className={`chat-session-timer-btn chat-session-timer-toggle ${chatSessionRunning ? 'running' : 'paused'}`}
                           onClick={() => setChatSessionRunning((prev) => !prev)}
                           title={chatSessionRunning ? 'Pause Timer' : 'Start Timer'}
                           aria-label={chatSessionRunning ? 'Pause Timer' : 'Start Timer'}
                         >
                           <FontAwesomeIcon icon={chatSessionRunning ? faPause : faPlay} />
                         </button>
                         <button
                           type="button"
                           className="chat-session-timer-btn chat-session-timer-reset"
                           onClick={() => {
                             setChatSessionRunning(false);
                             setChatSessionTimer(0);
                           }}
                           title="Reset Timer"
                           aria-label="Reset Timer"
                         >
                           <FontAwesomeIcon icon={faTimes} />
                         </button>
                       </div>
                     </div>
                     <button
                       type="button"
                       className="chat-session-followup-btn"
                       onClick={() => {
                         chatFollowUpWasRunningRef.current = chatSessionRunning;
                         setChatSessionRunning(false);
                         setShowChatFollowUpModal(true);
                       }}
                     >
                       Add Follow Up
                     </button>
                   </div>
                 )}
              </div>
            );
         })() : (
            <div className="snapshot-empty">
               <div className="snapshot-empty-container">
                  <p>Select a patient conversation or search for a patient to view their latest vitals.</p>
               </div>
            </div>
         )}
      </div>
      )}

      {showChatFollowUpModal && selectedPatientContext?.recordId && (
        <AddFollowUpModal
          isOpen={showChatFollowUpModal}
          onClose={() => {
            setShowChatFollowUpModal(false);
            if (chatFollowUpWasRunningRef.current) {
              setChatSessionRunning(true);
              chatFollowUpWasRunningRef.current = false;
            }
          }}
          practiceId={selectedPatientContext.practiceId || currentUser?.practice_id || reduxUser?.practice_id}
          patientId={selectedPatientContext.recordId}
          patientName={selectedPatientContext.name}
          initialTimerValue={chatSessionTimer}
          chatContext={chatFollowUpContext}
          onTimerSync={(seconds) => {
            setChatSessionTimer(Math.max(0, Number(seconds) || 0));
            setChatSessionRunning(true);
            chatFollowUpWasRunningRef.current = false;
          }}
          onSuccess={() => {}}
        />
      )}

      {/* MODAL */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>New Conversation</h3>
              <button className="modal-close" onClick={() => setIsModalOpen(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="modal-search">
              <input
                placeholder="Search by name, username, role, or practice..."
                autoFocus
                value={modalSearchTerm}
                onChange={(e) => setModalSearchTerm(e.target.value)}
              />
            </div>

            {!isLoadingUsers && !waitingForScPractice && modalRoleTabs.length > 1 && (
              <div className="modal-role-tabs" role="tablist" aria-label="Filter by role">
                {modalRoleTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={modalRoleTab === tab.id}
                    className={`modal-role-tab${modalRoleTab === tab.id ? ' active' : ''}`}
                    onClick={() => setModalRoleTab(tab.id)}
                  >
                    <span>{tab.label}</span>
                    <span className="modal-role-tab-count">{tab.count}</span>
                  </button>
                ))}
              </div>
            )}

            {showPracticeFilter && (
              <div className="modal-practice-filter">
                <label htmlFor="chat-modal-practice">
                  Practice
                  <span className="modal-practice-hint">
                    {isPracticeScopedSender
                      ? isSystemAdminSender
                        ? 'Select a practice to view staff for that practice'
                        : 'Select a practice to view assigned patients and staff'
                      : 'Select a practice to list Practice Admins'}
                  </span>
                </label>
                <select
                  id="chat-modal-practice"
                  value={modalPracticeId}
                  disabled={loadingModalPractices || isLoadingUsers}
                  onChange={(e) => handleModalPracticeChange(e.target.value)}
                >
                  <option value="">
                    {loadingModalPractices ? 'Loading practices…' : 'Select practice'}
                  </option>
                  {modalPractices.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.practice_name || p.name || `Practice ${p.id}`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="modal-body">
              {isLoadingUsers && <p style={{ padding: 16, textAlign: 'center' }}>Loading users...</p>}
              {!isLoadingUsers && waitingForScPractice && (
                <p style={{ padding: 16, textAlign: 'center' }}>
                  {isSystemAdminSender
                    ? 'You are assigned to multiple practices. Select a practice to view Super Admins, System Admins, Practice Admins, System Caregivers, Providers, and Caregivers.'
                    : 'You are assigned to multiple practices. Select a practice to view Super Admins, System Admins, Practice Admins, and your assigned patients.'}
                </p>
              )}
              {!isLoadingUsers &&
                !waitingForScPractice &&
                needsPracticeAdminFilter &&
                isPracticeAdminTab &&
                !modalPracticeId && (
                <p style={{ padding: 16, textAlign: 'center' }}>
                  Select a practice above to view Practice Admins.
                </p>
              )}
              {!isLoadingUsers &&
                !waitingForScPractice &&
                !(needsPracticeAdminFilter && isPracticeAdminTab && !modalPracticeId) &&
                tabFilteredAvailableUsers.length === 0 && (
                <p style={{ padding: 16, textAlign: 'center' }}>No users found.</p>
              )}
              {!isLoadingUsers &&
                !waitingForScPractice &&
                !(needsPracticeAdminFilter && isPracticeAdminTab && !modalPracticeId) &&
                tabFilteredAvailableUsers.map((u) => renderModalUserRow(u))}
            </div>
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        show={deleteModalState.show}
        title={deleteModalState.title}
        message={deleteModalState.message}
        warning={deleteModalState.warning}
        confirmLabel={deleteModalState.confirmLabel}
        secondaryConfirmLabel={deleteModalState.secondaryConfirmLabel}
        isDeleting={deleteModalState.isDeleting}
        onConfirm={deleteModalState.onConfirm}
        onSecondaryConfirm={deleteModalState.onSecondaryConfirm}
        onCancel={closeDeleteModal}
      />

    </div>
  );
};

export default ChatSystem;
